/**
 * Shopify Website Tracking Controller
 * Main tracking system for Shopify store with Facebook Pixel integration
 * Handles custom events, virtual tryon tracking, and e-commerce interactions
 * Version: 2.0.0 - Enhanced security and fixed duplicate tracking issues
 */

(function () {
    'use strict';

    // Prevent multiple initializations
    if (window._shopifyTrackingInitialized) {
        console.warn('[Shopify Tracking] Already initialized, skipping duplicate initialization');
        return;
    }
    window._shopifyTrackingInitialized = true;

    // Configuration (External PHP CAPI handler with Shopify settings)
    const config = {
        debug: window.shopifyTrackingConfig?.debug || false,
        currency: window.shopifyTrackingConfig?.currency || 'USD',
        capiEnabled: window.shopifyTrackingConfig?.capi_enabled || false,
        capiAccessToken: window.shopifyTrackingConfig?.capi_access_token || '',
        capiDatasetId: window.shopifyTrackingConfig?.capi_dataset_id || '',
        capiTestEventCode: window.shopifyTrackingConfig?.capi_test_event_code || '',
        capiSecret: window.shopifyTrackingConfig?.capi_secret || 'your-secret-key-here-change-this', // Match PHP
        ajaxUrl: 'https://reporting.kojandat.com/tracking/honorine_tracking.php', // External CAPI handler
        nonce: window.shopifyTrackingConfig?.nonce || '', // For potential future use
        siteUrl: window.shopifyTrackingConfig?.shop_domain || window.location.origin,
        // Custom event values for your store (non-commerce events only)
        eventValues: {
            'virtual_tryon_start': 15,
            'virtual_tryon_complete': 25,
            'virtual_tryon_share': 10,
            'size_guide_view': 5,
            'wishlist_add': 12,
            'account_creation': 20,
            'newsletter_signup': 18,
            'chat_initiated': 15,
            'return_request': -5,
            'about_page_view': 3,
            'contact_page_view': 8,
            'size_guide_page_view': 5,
            'search_page_view': 2,
            'social_share': 6,
            'form_abandonment': -2,
            'exit_intent': 1
        }
    };

    // Get Shopify context data
    const shopifyContext = {
        shop: Shopify?.shop || window.ShopifyAnalytics?.meta?.shop || '',
        currency: Shopify?.currency?.active || 'EUR',
        customer_id: window.ShopifyAnalytics?.meta?.customer?.id || null,
        page_type: window.ShopifyAnalytics?.meta?.page?.pageType || 'unknown',
        product_id: window.ShopifyAnalytics?.meta?.product?.id || null,
        collection_id: window.ShopifyAnalytics?.meta?.collection?.id || null,
        cart_total: window.ShopifyAnalytics?.meta?.cart?.total || 0
    };

    // Module initialization tracking
    const moduleStates = {
        interaction: false,
        virtualTryon: false,
        form: false
    };

    /**
     * Debug logging
     */
    function log(message, data = null) {
        if (config.debug) {
            const debugInfo = data ? { ...data } : {};
            if (config.capiTestEventCode) {
                debugInfo.testEventCode = config.capiTestEventCode;
            }
            console.log('[Shopify Tracking]', message, debugInfo || '');
        }
    }

    /**
     * Check if Facebook Pixel is loaded and ready (initialized by Shopify)
     */
    function isPixelReady() {
        return typeof window.fbq === 'function' && window.fbq.loaded === true;
    }

    /**
     * Generate unique event ID for deduplication using crypto API
     */
    function generateEventId(eventName, customData = {}) {
        const timestamp = Date.now();
        let random;
        
        try {
            // Use crypto API for better randomness
            const array = new Uint32Array(1);
            crypto.getRandomValues(array);
            random = array[0].toString(36);
        } catch (e) {
            // Fallback for older browsers
            random = Math.random().toString(36).substr(2, 6);
        }
        
        const dataHash = JSON.stringify(customData).length.toString(36);
        return `${eventName.toLowerCase()}_${random}_${dataHash}_${timestamp}`;
    }

    /**
     * Hash value using Web Crypto API for client-side PII hashing
     */
    async function hashValue(value) {
        if (!value) return null;
        
        try {
            const msgBuffer = new TextEncoder().encode(value.toLowerCase().trim());
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (e) {
            log('Error hashing value:', e);
            return null;
        }
    }

    /**
     * Generate HMAC signature for request authentication
     */
    async function generateSignature(payload, timestamp) {
        try {
            const message = timestamp + '.' + payload;
            const encoder = new TextEncoder();
            const keyData = encoder.encode(config.capiSecret);
            const messageData = encoder.encode(message);
            
            const key = await crypto.subtle.importKey(
                'raw',
                keyData,
                { name: 'HMAC', hash: 'SHA-256' },
                false,
                ['sign']
            );
            
            const signature = await crypto.subtle.sign('HMAC', key, messageData);
            return Array.from(new Uint8Array(signature))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
        } catch (e) {
            log('Error generating signature:', e);
            throw new Error('Failed to generate request signature');
        }
    }

    /**
     * Get Facebook Click ID from URL or session
     */
    function getFbclid() {
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');

        if (fbclid) {
            if (typeof Storage !== 'undefined') {
                sessionStorage.setItem('fbclid', fbclid);
            }
            return fbclid;
        }

        if (typeof Storage !== 'undefined') {
            const stored = sessionStorage.getItem('fbclid');
            if (stored) return stored;
        }

        return null;
    }

    /**
     * Get Facebook Browser ID (fbp cookie)
     */
    function getFbp() {
        if (typeof window.fbq === 'function' && window.fbq.getState) {
            try {
                const state = window.fbq.getState();
                return state.pixels?.[0]?.userData?.fbp || null;
            } catch (e) {
                // Fallback to cookie reading
                return getCookie('_fbp');
            }
        }
        return getCookie('_fbp');
    }

    /**
     * Get cookie value by name
     */
    function getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    /**
     * Get customer data from Shopify context
     */
    async function getCustomerData() {
        const customerData = {};

        // Try to get customer email from various sources
        let email = null;
        if (window.Shopify?.checkout?.email) {
            email = window.Shopify.checkout.email;
        } else if (window.ShopifyAnalytics?.meta?.customer?.email) {
            email = window.ShopifyAnalytics.meta.customer.email;
        }

        // Try to get customer phone
        let phone = null;
        if (window.Shopify?.checkout?.phone) {
            phone = window.Shopify.checkout.phone;
        } else if (window.ShopifyAnalytics?.meta?.customer?.phone) {
            phone = window.ShopifyAnalytics.meta.customer.phone;
        }

        // Hash PII on client-side for better privacy
        if (email) {
            customerData.customer_email = email;
            customerData.hashed_email = await hashValue(email);
        }

        if (phone) {
            const cleanPhone = phone.replace(/\D/g, '');
            if (cleanPhone) {
                customerData.customer_phone = phone;
                customerData.hashed_phone = await hashValue(cleanPhone);
            }
        }

        // Customer ID
        if (window.ShopifyAnalytics?.meta?.customer?.id) {
            customerData.customer_id = window.ShopifyAnalytics.meta.customer.id;
        }

        // Try to get customer data from forms (for logged-out users)
        const emailInputs = document.querySelectorAll('input[type="email"]');
        const phoneInputs = document.querySelectorAll('input[type="tel"], input[name*="phone"]');
        
        if (emailInputs.length > 0 && emailInputs[0].value && !email) {
            const formEmail = emailInputs[0].value;
            customerData.form_email = formEmail;
            customerData.hashed_email = await hashValue(formEmail);
        }
        
        if (phoneInputs.length > 0 && phoneInputs[0].value && !phone) {
            const formPhone = phoneInputs[0].value.replace(/\D/g, '');
            if (formPhone) {
                customerData.form_phone = phoneInputs[0].value;
                customerData.hashed_phone = await hashValue(formPhone);
            }
        }

        return customerData;
    }

    /**
     * Send event to Facebook Pixel
     */
    function sendPixelEvent(eventName, eventData = {}, eventId = null) {
        if (!isPixelReady()) {
            log(`Facebook Pixel not loaded, skipping ${eventName}`);
            return false;
        }

        try {
            eventId = eventId || generateEventId(eventName, eventData);

            const fbclid = getFbclid();
            if (fbclid) {
                eventData.fbclid = fbclid;
            }

            // Add Shopify context to event data
            eventData.shop_domain = shopifyContext.shop;
            if (shopifyContext.customer_id) {
                eventData.customer_id = shopifyContext.customer_id;
            }

            const pixelOptions = { eventID: eventId };

            // Use trackCustom for non-standard events, track for standard events
            if (isStandardFacebookEvent(eventName)) {
                window.fbq('track', eventName, eventData, pixelOptions);
            } else {
                window.fbq('trackCustom', eventName, eventData, pixelOptions);
            }

            log(`${eventName} event sent`, {
                eventData,
                eventId,
                method: isStandardFacebookEvent(eventName) ? 'track' : 'trackCustom',
                capiEnabled: config.capiEnabled
            });

            return eventId;

        } catch (error) {
            log(`Error sending ${eventName}:`, error);
            return false;
        }
    }

    /**
     * Check if event name is a standard Facebook event
     */
    function isStandardFacebookEvent(eventName) {
        const standardEvents = [
            'PageView',
            'ViewContent', 
            'Search',
            'AddToCart',
            'AddToWishlist',
            'InitiateCheckout',
            'AddPaymentInfo',
            'Purchase',
            'Lead',
            'CompleteRegistration',
            'Contact',
            'CustomizeProduct',
            'Donate',
            'FindLocation',
            'Schedule',
            'StartTrial',
            'SubmitApplication',
            'Subscribe'
        ];
        
        return standardEvents.includes(eventName);
    }

    /**
     * Enhanced sendToServer with signature authentication
     */
    async function sendToServer(eventName, eventData, eventId = null) {
        if (!isPixelReady() || !config.capiEnabled) {
            return false;
        }

        if (!config.capiAccessToken || !config.capiDatasetId) {
            log('CAPI configuration missing - need access token and dataset ID');
            return false;
        }

        try {
            // Get customer data from Shopify context
            const customerData = await getCustomerData();

            // Enhance event data with client context
            const enhancedEventData = {
                ...eventData,
                event_id: eventId,
                page_location: window.location.href,
                client_user_agent: navigator.userAgent,
                referrer: document.referrer || null,
                fbclid: getFbclid(),
                fbp: getFbp(),
                timestamp: Date.now()
            };

            const payload = {
                event_name: eventName,
                event_data: enhancedEventData,
                capi_config: {
                    access_token: config.capiAccessToken,
                    dataset_id: config.capiDatasetId,
                    test_event_code: config.capiTestEventCode
                },
                shopify_context: {
                    ...shopifyContext,
                    ...customerData
                }
            };

            const payloadString = JSON.stringify(payload);
            const timestamp = Math.floor(Date.now() / 1000);
            
            // Generate signature for authentication
            const signature = await generateSignature(payloadString, timestamp);
            
            if (config.debug && config.capiTestEventCode) {
                log(`CAPI Test Mode: Sending to external handler with test code ${config.capiTestEventCode}`, {
                    eventName,
                    endpoint: config.ajaxUrl,
                    testEventCode: config.capiTestEventCode,
                    hasSignature: !!signature
                });
            }

            const response = await fetch(config.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-Signature': signature,
                    'X-Timestamp': timestamp.toString()
                },
                body: payloadString
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (config.debug) {
                log(`CAPI event sent successfully: ${eventName}`, {
                    success: data.success,
                    eventId: eventId,
                    facebookResponse: data.facebook_response,
                    server: data.server || 'unknown'
                });
            }

        } catch (error) {
            log(`CAPI error for ${eventName}:`, {
                error: error.message,
                endpoint: config.ajaxUrl,
                eventId: eventId
            });
        }

        return true;
    }

    /**
     * Track dual events (client + server)
     */
    function trackDualEvent(eventName, eventData = {}) {
        if (!isPixelReady()) {
            log(`Event blocked - no consent: ${eventName}`);
            return false;
        }

        const eventId = generateEventId(eventName, eventData);

        // Send to client-side pixel
        sendPixelEvent(eventName, eventData, eventId);

        // Send to server for CAPI
        if (config.capiEnabled) {
            sendToServer(eventName, eventData, eventId);
        }

        return eventId;
    }

    /**
     * Track custom events with predefined values
     */
    function trackCustomEvent(eventName, customData = {}) {
        if (!isPixelReady()) {
            log(`Custom event blocked - no consent: ${eventName}`);
            return false;
        }

        try {
            const eventData = {
                content_category: 'custom_interaction',
                event_type: eventName,
                value: config.eventValues[eventName] || 0,
                currency: config.currency,
                page_type: shopifyContext.page_type,
                ...customData
            };

            trackDualEvent(eventName, eventData);

            log(`Custom event tracked: ${eventName}`, eventData);
            return true;
        } catch (error) {
            log(`Error tracking custom event ${eventName}:`, error);
            return false;
        }
    }

    /**
     * Track virtual tryon events (high priority)
     */
    function trackVirtualTryonEvent(action, productData = {}) {
        if (!isPixelReady()) {
            log(`Virtual tryon event blocked - no consent: ${action}`);
            return false;
        }

        try {
            const eventName = `virtual_tryon_${action}`;
            const eventData = {
                content_name: `Virtual Tryon - ${action}`,
                content_category: 'virtual_tryon',
                event_type: `virtual_tryon_${action}`,
                value: config.eventValues[`virtual_tryon_${action}`] || 15,
                currency: config.currency,
                page_type: 'virtual_tryon',
                ...productData
            };

            // Send the actual event name, not a generic one
            trackDualEvent(eventName, eventData);

            log(`Virtual tryon ${action} tracked`, eventData);
            return true;
        } catch (error) {
            log(`Error tracking virtual tryon ${action}:`, error);
            return false;
        }
    }

    /**
     * Wait for pixel to be ready
     */
    function waitForPixel(attempts = 0, maxAttempts = 150) { // Reduced timeout to 15 seconds
        if (isPixelReady()) {
            initializeTracking();
            return;
        }

        if (attempts < maxAttempts) {
            setTimeout(() => waitForPixel(attempts + 1, maxAttempts), 100);
        } else {
            log('Facebook Pixel not detected after 15 seconds');
            // Initialize basic tracking without pixel
            initializeBasicTracking();
        }
    }

    /**
     * Initialize tracking system (does NOT initialize Facebook Pixel - Shopify handles that)
     */
    function initializeTracking() {
        // Check for duplicate pixel initialization
        if (window._fbPixelInitCount) {
            window._fbPixelInitCount++;
            log(`WARNING: Facebook Pixel initialization detected ${window._fbPixelInitCount} times`, {
                warning: 'Multiple pixel initializations detected',
                recommendation: 'Check for duplicate Facebook Pixel code'
            });
        } else {
            window._fbPixelInitCount = 1;
        }

        log('Initializing Shopify custom tracking (using existing Shopify pixel)', {
            pixelReady: isPixelReady(),
            capiEnabled: config.capiEnabled,
            debugMode: config.debug,
            testEventCode: config.capiTestEventCode || 'none',
            shopifyContext: shopifyContext,
            pixelInitCount: window._fbPixelInitCount
        });

        // Verify Shopify has properly loaded the pixel
        if (!isPixelReady()) {
            log('WARNING: Facebook Pixel not ready - Shopify Facebook app may not be properly configured');
            return;
        }

        // Initialize page-specific tracking
        initializePageTracking();
        
        // Initialize modules (with deduplication)
        initializeModules();
    }

    /**
     * Initialize basic tracking without pixel
     */
    function initializeBasicTracking() {
        log('Initializing basic tracking without pixel');
        // Still initialize modules for future pixel loading
        initializeModules();
    }

    /**
     * Initialize all tracking modules with deduplication
     */
    function initializeModules() {
        // Initialize interaction tracking only once
        if (!moduleStates.interaction && window.initializeInteractionTracking) {
            moduleStates.interaction = true;
            log('Initializing Interaction tracking module');
            window.initializeInteractionTracking();
        }
        
        // Initialize virtual tryon tracking only once and only on relevant pages
        if (!moduleStates.virtualTryon && isVirtualTryonPage() && window.initializeVirtualTryonTracking) {
            moduleStates.virtualTryon = true;
            log('Initializing Virtual Tryon tracking module');
            window.initializeVirtualTryonTracking();
        }
        
        // Initialize form tracking only once
        if (!moduleStates.form && window.initializeFormTracking) {
            moduleStates.form = true;
            log('Initializing Form tracking module');
            window.initializeFormTracking();
        }
    }

    /**
     * Check if current page is virtual tryon
     */
    function isVirtualTryonPage() {
        return window.location.pathname.includes('virtual-tryon') ||
               window.location.pathname.includes('essayage-virtuel') ||
               document.querySelector('.virtual-tryon-section') !== null ||
               window.virtualTryOn !== undefined;
    }

    /**
     * Initialize page-specific tracking (only custom events, not standard e-commerce)
     * FIXED: Removed duplicate virtual tryon page tracking
     */
    function initializePageTracking() {
        // Only track custom page events that Shopify doesn't handle
        const pageType = shopifyContext.page_type;
        
        // Track special non-commerce pages
        if (pageType === 'page') {
            const pageName = document.title.toLowerCase();
            if (pageName.includes('about') || pageName.includes('à propos')) {
                trackCustomEvent('about_page_view', {
                    content_name: document.title
                });
            } else if (pageName.includes('contact')) {
                trackCustomEvent('contact_page_view', {
                    content_name: document.title
                });
            } else if (pageName.includes('size') || pageName.includes('taille')) {
                trackCustomEvent('size_guide_page_view', {
                    content_name: document.title
                });
            }
        } else if (pageType === 'search') {
            trackCustomEvent('search_page_view', {
                search_query: getSearchQuery()
            });
        }
        
        // REMOVED: Virtual tryon page tracking
        // This is now handled exclusively by the virtual_tryon_tracking.js module
        // to avoid duplicate events
    }

    /**
     * Helper function to get search query from URL
     */
    function getSearchQuery() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('q') || urlParams.get('query') || '';
    }

    /**
     * Public API for Shopify Tracking (Custom Events Only)
     */
    window.ShopifyTracking = {
        // Core functions
        trackCustomEvent,
        trackDualEvent,
        trackVirtualTryonEvent,
        
        // Custom business events (non-commerce)
        trackNewsletterSignup: (data = {}) => trackCustomEvent('newsletter_signup', data),
        trackAccountCreation: (data = {}) => trackCustomEvent('account_creation', data),
        trackWishlistAdd: (data = {}) => trackCustomEvent('wishlist_add', data),
        trackSizeGuideView: (data = {}) => trackCustomEvent('size_guide_view', data),
        trackChatInitiated: (data = {}) => trackCustomEvent('chat_initiated', data),
        trackReturnRequest: (data = {}) => trackCustomEvent('return_request', data),
        trackSocialShare: (data = {}) => trackCustomEvent('social_share', data),
        trackFormAbandonment: (data = {}) => trackCustomEvent('form_abandonment', data),
        trackExitIntent: (data = {}) => trackCustomEvent('exit_intent', data),
        
        // Virtual tryon specific (high priority)
        trackVirtualTryonStart: (productData = {}) => trackVirtualTryonEvent('start', productData),
        trackVirtualTryonComplete: (productData = {}) => trackVirtualTryonEvent('complete', productData),
        trackVirtualTryonShare: (productData = {}) => trackVirtualTryonEvent('share', productData),
        
        // Utility functions
        isPixelReady,
        getShopifyContext: () => shopifyContext,
        getConfig: () => config,
        getModuleStates: () => moduleStates,
        isDebugMode: () => config.debug,
        getTestEventCode: () => config.capiTestEventCode,
        hashValue, // Expose for other modules
        
        // Debug
        log
    };

    // Auto-initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(waitForPixel, 1000); // Give Shopify time to load pixel
        });
    } else {
        setTimeout(waitForPixel, 1000);
    }

})();